import { memo } from 'react';

import { List, Text } from '@zillow/constellation-10';

import { useSelector } from '../../../../selectors/useSelector';

import { ROOM_FOR_RENT_FLOW } from '../../../../containers/CreateListingPage/constants';

import {
  checkDisplayForRentByChangeWarning,
  checkIsApplicationAcceptedValueChanging,
  getRoomForRentFlow,
} from '../../../../containers/CreateListingPage/utils';

interface Props {
  roomForRentFlow: string;
  nextForRentByVal: string;
}

const hasTrueValueInObj = (obj: Record<string, unknown>): boolean => {
  return Object.values(obj).some((value) => {
    return value === true;
  });
};

const ForRentByWarning = ({ roomForRentFlow, nextForRentByVal }: Props): JSX.Element => {
  const { isApplicationsAccepted, roomForRent } = useSelector((state) => state.properties.currentProperty);

  const {
    hasContentObj,
    hasContentObj: { roommateCount, roommateTotalCount, roommateDetailsDescription, renterAgeAndGenderPreferred },
  } = checkDisplayForRentByChangeWarning({
    roomForRentFlow,
    roomForRent: { ...roomForRent },
  });

  const { isApplicationAcceptedValueChanging, isApplicationsAcceptedChangingToTrueValue } =
    checkIsApplicationAcceptedValueChanging({
      isApplicationsAccepted,
      nextForRentByVal,
    });

  const isFlowChanging = roomForRentFlow !== getRoomForRentFlow(nextForRentByVal);
  const hasContentThatWillBeDeleted = isFlowChanging && hasTrueValueInObj(hasContentObj);

  const displayIfPreviouslyEnteredInformation = hasContentThatWillBeDeleted;

  return (
    <div data-testid="ForRentByWarning">
      {displayIfPreviouslyEnteredInformation && (
        <Text css={{ marginBottom: '400' }} asChild>
          <p>
            If you previously entered the following information, it will be deleted when you change who&apos;s listing:
          </p>
        </Text>
      )}

      {isFlowChanging && roomForRentFlow === ROOM_FOR_RENT_FLOW.IRENE_FLOW && (
        <List.Root>{roommateTotalCount && <List.Item>Roommate count</List.Item>}</List.Root>
      )}

      {isFlowChanging && roomForRentFlow === ROOM_FOR_RENT_FLOW.RYAN_FLOW && (
        <List.Root>
          {roommateCount && <List.Item>Roommate count</List.Item>}
          {roommateDetailsDescription && <List.Item>Roommates description</List.Item>}
          {renterAgeAndGenderPreferred && <List.Item>Renter age and gender preferred</List.Item>}
        </List.Root>
      )}

      {isApplicationAcceptedValueChanging && (
        <>
          {isApplicationsAcceptedChangingToTrueValue ? (
            <Text asChild>
              <p>You&apos;ll be automatically opted into our applications feature. Opt out at any time.</p>
            </Text>
          ) : (
            <Text asChild>
              <p>
                You&apos;ll be automatically opted out of our applications feature. Use of Zillow Applications is not
                currently available for tenants.
              </p>
            </Text>
          )}
        </>
      )}
    </div>
  );
};

export default memo(ForRentByWarning);
